/**
 * Hands Panel
 * Every opponent's hole cards at full size, as much as the viewer is
 * allowed to see — opened by tapping an insight special (Oracle,
 * Soothsayer, Spy, Omnipotent) once it's active, since the seat tiles'
 * cards are far too small to read. Each special sits on its own card's
 * side, as in the hand. Tap anywhere to close.
 */

import React from 'react';
import type { Player } from '../../store/types';
import PlayingCard from './PlayingCard';
import SpecialCard from './SpecialCard';
import { specialOfCard } from './specialOfCard';
import { formatChips } from '../../utils/format';

interface HandsPanelProps {
  title: string;
  /** The opponents, in seat order. */
  players: Player[];
  onClose: () => void;
}

const CARD = 'w-[44px]';

const STATUS: Partial<Record<Player['status'], string>> = {
  FOLDED: 'Folded',
  ALL_IN: 'All in',
  SITTING_OUT: 'Sitting out',
};

const HandsPanel: React.FC<HandsPanelProps> = ({ title, players, onClose }) => (
  <div
    data-testid="hands-panel"
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3"
    onPointerDown={onClose}
  >
    <div className="w-full max-w-sm max-h-full overflow-y-auto rounded-xl border border-amber-700/60 bg-gray-900 p-3 shadow-2xl">
      <div className="mb-2 text-center text-sm font-bold text-amber-200">{title}</div>
      <div className="flex flex-col gap-2">
        {players.map((player) => {
          const hole = (player.holeCards ?? [null, null]).slice(0, 2);
          const [left, right] = [specialOfCard(hole[0]), specialOfCard(hole[1])];
          return (
            <div
              key={player.id}
              data-testid="hands-panel-player"
              className={`flex items-center gap-2 rounded-lg border border-gray-800 bg-gray-950/60 p-1.5 ${
                player.status === 'FOLDED' ? 'opacity-60' : ''
              }`}
            >
              <div className="w-20 shrink-0 leading-tight">
                <div className="truncate text-xs font-semibold text-white">
                  <span className="text-[10px] text-gray-500">{player.seatPosition + 1} </span>
                  {player.username}
                </div>
                <div className="text-[11px] font-semibold text-yellow-400">{formatChips(player.chipCount)}</div>
                {STATUS[player.status] && <div className="text-[10px] text-gray-500">{STATUS[player.status]}</div>}
              </div>
              <div className="flex items-center gap-1">
                {left && <SpecialCard special={left} showName={false} className={CARD} />}
                {hole.map((card, i) => (
                  <PlayingCard key={i} card={card ?? undefined} faceDown={!card} className={CARD} />
                ))}
                {right && <SpecialCard special={right} showName={false} className={CARD} />}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-center text-[10px] text-gray-500">Tap anywhere to close</p>
    </div>
  </div>
);

export default HandsPanel;
